import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Snowflake, Heart, Sparkles } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import Bottle from '@/components/ui/Bottle';
import { PRODUCTS } from '@/data/products';

export default function OurStory() {
  return (
    <div data-testid="page-our-story">
      <PageHeader
        eyebrow="Our Story"
        title="A kitchen,"
        accent="a craft, a quiet rebellion."
        description="Sip Theory was born from a refusal to settle for plastic-sweet drinks. We make beverages the slow way — real fruit, honest recipes, and a cold chain that never breaks."
      />

      {/* Story long-form */}
      <section className="container-x py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 space-y-8">
          <Chapter
            number="01"
            title="Where it began"
            body="It started in a small kitchen with mismatched jars and a notepad full of crossed-out recipes. We were looking for the one thing the shelves had stopped selling: a drink that tastes like the fruit it claims, and leaves you better than it found you."
          />
          <Chapter
            number="02"
            title="The honest recipe"
            body="Every Sip Theory bottle holds only what we can name. Real fruit. Real nuts. Real herbs. Nothing artificial, no shortcuts, no added sugar. If a recipe needs more than ten ingredients, it doesn't make it into a bottle."
          />
          <Chapter
            number="03"
            title="Cold from kitchen to hand"
            body="We refrigerate from the moment a bottle is sealed. No preservatives, no shortcuts — just a short, honest cold chain that protects every nutrient and every note."
          />
          <Chapter
            number="04"
            title="A theory worth sipping"
            body="If goodness has to taste like a punishment, something has gone wrong. Sip Theory exists to prove the opposite — and we'd love for you to taste the proof."
          />
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-28 space-y-5">
          <div className="bottle-stage rounded-3xl p-10 grain relative">
            <div className="flex justify-center items-end gap-3">
              {PRODUCTS.slice(0, 3).map((p, i) => (
                <div key={p.id} className={i === 1 ? '-translate-y-6' : ''}>
                  <Bottle product={p} size="md" floating />
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Pill icon={<Leaf className="w-4 h-4" />} label="Real Ingredients" />
            <Pill icon={<Sparkles className="w-4 h-4" />} label="No Added Sugar" />
            <Pill icon={<Snowflake className="w-4 h-4" />} label="Refrigerated" />
            <Pill icon={<Heart className="w-4 h-4" />} label="Made With Love" />
          </div>
        </aside>
      </section>

      {/* Manifesto */}
      <section className="container-x py-20">
        <div className="glass-card rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-radial-spot pointer-events-none" />
          <span className="eyebrow">Our Manifesto</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-bone-50 max-w-3xl mx-auto mt-5 leading-tight">
            “Goodness should taste like <span className="italic text-sage-300">goodness</span>. Not a compromise.”
          </h2>
          <Link to="/shop" className="btn-primary mt-10 inline-flex" data-testid="story-shop-cta">
            Taste the theory <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

const Chapter = ({ number, title, body }) => (
  <article className="glass-card rounded-3xl p-8 lg:p-10">
    <span className="text-[11px] tracking-[0.32em] uppercase text-gold/90">Chapter {number}</span>
    <h3 className="font-display text-2xl lg:text-3xl text-bone-50 mt-3">{title}</h3>
    <p className="text-bone-200/90 mt-4 leading-relaxed">{body}</p>
  </article>
);

const Pill = ({ icon, label }) => (
  <div className="glass-soft rounded-full px-4 py-3 flex items-center gap-3 text-sm text-bone-100">
    <span className="w-8 h-8 rounded-full border border-gold/30 flex items-center justify-center text-gold">{icon}</span>
    {label}
  </div>
);
